/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * themeConfig.js — UNICA sorgente dei valori di branding e delle variabili di
 * business del prodotto White-Label.
 *
 * Personalizzazione "in un click": modifica QUESTO file (oppure lascia che
 * `public/brand-config.json` / il pannello Amministrazione → Brand lo sovrascrivano
 * a runtime). Nessun componente deve contenere HEX, nomi o URL di brand hardcoded:
 * deve leggere da `useBrand()` (contesto runtime) oppure da `THEME_CONFIG`.
 *
 * Precedenza a runtime (vedi lib/whitelabel.js):
 *   DB (/api/brand-settings)  >  public/brand-config.json  >  THEME_CONFIG
 *
 * Variabili d'ambiente opzionali (build-time) che sovrascrivono i default:
 *   REACT_APP_BRAND_NAME, REACT_APP_BRAND_SHORT_NAME, REACT_APP_BRAND_PRIMARY,
 *   REACT_APP_BRAND_SECONDARY, REACT_APP_BRAND_ACCENT, REACT_APP_BRAND_LOGO_URL
 */

const env = (key, fallback) => {
  const v = typeof process !== 'undefined' ? process.env?.[key] : undefined;
  return v && String(v).trim() ? String(v).trim() : fallback;
};

export const THEME_CONFIG = Object.freeze({
  // --------------------------------------------------------------- identità
  appName: env('REACT_APP_BRAND_NAME', 'SanitàNexus'),
  appShortName: env('REACT_APP_BRAND_SHORT_NAME', 'Nexus'),
  tagline: 'Il tuo centro di salute integrato',
  saasTagline: 'Ecosistema digitale e servizi sanitari territoriali',
  logoUrl: env('REACT_APP_BRAND_LOGO_URL', null),
  faviconUrl: null,
  // Tenant key per l'AI Usage Counter (header `x-clinic-id`). Se assente, nei cloni
  // white-label viene derivata dal nome clinica (slug); in modalità SaaS vale "default".
  clinicId: env('REACT_APP_CLINIC_ID', null),

  // --------------------------------------------------------------- palette (HEX)
  colors: Object.freeze({
    primary: env('REACT_APP_BRAND_PRIMARY', '#0F766E'),     // Teal medical-grade
    secondary: env('REACT_APP_BRAND_SECONDARY', '#059669'), // Emerald — stati positivi
    accent: env('REACT_APP_BRAND_ACCENT', '#A7F3D0'),       // tinta decorativa
    darkPrimary: null,                                       // opzionale: primario esplicito per dark mode
    // Toni neutri usati da grafici/PDF quando serve un HEX (non un token CSS)
    ink: '#0F1729',
    muted: '#64748B',
    surface: '#F5F7F9',
    border: '#E5E7EB',
  }),

  // --------------------------------------------------------------- dati legali default (placeholder clinic-owned)
  legal: Object.freeze({
    owner: 'La tua clinica S.r.l.',
    address: 'Via Roma 1, 20100 Milano (MI), Italia',
    vat: 'IT00000000000',
    fiscalCode: null,
    dpo: 'dpo@example.com',
    contactEmail: 'info@example.com',
    contactPhone: null,
  }),

  // --------------------------------------------------------------- listino
  priceList: Object.freeze({
    currency: 'EUR',
    defaultVisitPrice: 25.0,
    doctorAnnualSubscription: 49.9,
    patientPremiumMonthly: 4.99,
    platformFeePct: 15,
    freeConsultationsPerMonth: 2,
    items: [],
  }),

  // --------------------------------------------------------------- scaglioni ISEE (scala mobile)
  iseeTiers: Object.freeze([
    { id: 'low', threshold: 15000, discount_pct: 40, label: 'ISEE bassa' },
    { id: 'mid_low', threshold: 20000, discount_pct: 25, label: 'ISEE medio-bassa' },
    { id: 'mid', threshold: 30000, discount_pct: 10, label: 'ISEE media' },
    { id: 'high', threshold: null, discount_pct: 0, label: 'ISEE alta' },
  ]),

  // --------------------------------------------------------------- feature flag prodotto
  features: Object.freeze({
    localAI: true,
    offlineMode: true,
    patientPortalE2EE: true,
    fiscalModule: true,
  }),
});

/**
 * Mappa THEME_CONFIG nella forma "flat" usata dal BrandContext (`useBrand()`),
 * così i componenti esistenti continuano a leggere `brand.primaryColor`, ecc.
 */
export const themeConfigToBrand = (cfg = THEME_CONFIG) => Object.freeze({
  clinicId: cfg.clinicId,
  clinicName: cfg.appName,
  clinicShortName: cfg.appShortName,
  tagline: cfg.saasTagline,
  primaryColor: cfg.colors.primary,
  secondaryColor: cfg.colors.secondary,
  primaryColorAlt: cfg.colors.secondary, // alias legacy
  accentColor: cfg.colors.accent,
  darkPrimaryColor: cfg.colors.darkPrimary,
  logoUrl: cfg.logoUrl,
  faviconUrl: cfg.faviconUrl,
  contactEmail: cfg.legal.contactEmail,
  contactPhone: cfg.legal.contactPhone,
  legalOwner: cfg.legal.owner,
  legalAddress: cfg.legal.address,
  legalVat: cfg.legal.vat,
  legalFiscalCode: cfg.legal.fiscalCode,
  legalDpo: cfg.legal.dpo,
  priceList: { ...cfg.priceList },
  iseeTiers: cfg.iseeTiers.map((t) => ({ ...t })),
  features: { ...cfg.features },
});

/** HEX del primario di brand da usare SOLO dove un token CSS non è applicabile (canvas, PDF, email). */
export const BRAND_PRIMARY_HEX = THEME_CONFIG.colors.primary;
export const BRAND_SECONDARY_HEX = THEME_CONFIG.colors.secondary;
export const BRAND_ACCENT_HEX = THEME_CONFIG.colors.accent;

/**
 * Legge a runtime il colore di brand effettivo (dopo override DB/JSON) dalle
 * CSS variables scritte da `applyBrandTheme`. Fallback al default statico.
 */
export const readBrandHex = (which = 'primary') => {
  const fallback = { primary: BRAND_PRIMARY_HEX, secondary: BRAND_SECONDARY_HEX, accent: BRAND_ACCENT_HEX }[which] || BRAND_PRIMARY_HEX;
  if (typeof document === 'undefined') return fallback;
  const varName = { primary: '--wl-primary', secondary: '--wl-primary-alt', accent: '--wl-accent' }[which] || '--wl-primary';
  const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return v || fallback;
};

export default THEME_CONFIG;
